/**
 * Resolve logged-in profile fields into journey wizard defaults.
 * Guests get no prefill (caller skips). Missing/invalid fields are omitted.
 */
import moment from 'moment';
import {
  JOURNEY_AGE_MAX,
  JOURNEY_AGE_MIN,
  JOURNEY_HEIGHT_MAX,
  JOURNEY_HEIGHT_MIN,
  JOURNEY_WEIGHT_MAX,
  JOURNEY_WEIGHT_MIN,
  type JourneyGender,
} from './config';

export type JourneyPrefillSource = {
  gender?: string | null;
  date_of_birth?: string | null;
  birth_date?: string | null;
  height?: string | number | null;
  weight?: string | number | null;
  preferences?: Record<string, unknown> | null;
};

export type JourneyPrefill = {
  gender?: JourneyGender;
  birth?: Date;
  height_cm?: number;
  weight_kg?: number;
};

function parse_number(raw: unknown): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function pick_pref(source: JourneyPrefillSource, key: string): unknown {
  const prefs = source.preferences;
  if (prefs && typeof prefs === 'object' && key in prefs) {
    return (prefs as Record<string, unknown>)[key];
  }
  return undefined;
}

export function resolveJourneyPrefill(source: JourneyPrefillSource | null | undefined): JourneyPrefill {
  if (!source) {
    return {};
  }

  const out: JourneyPrefill = {};

  const gender_raw = String(source.gender ?? '').trim().toLowerCase();
  if (gender_raw === 'male' || gender_raw === 'female') {
    out.gender = gender_raw;
  }

  const dob_raw =
    source.date_of_birth ||
    source.birth_date ||
    (typeof pick_pref(source, 'date_of_birth') === 'string' ? String(pick_pref(source, 'date_of_birth')) : '') ||
    (typeof pick_pref(source, 'birth_date') === 'string' ? String(pick_pref(source, 'birth_date')) : '');
  if (dob_raw) {
    const m = moment(dob_raw, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'DD-MM-YYYY'], true);
    if (m.isValid()) {
      const age = moment().diff(m, 'years');
      if (age >= JOURNEY_AGE_MIN && age <= JOURNEY_AGE_MAX) {
        out.birth = m.toDate();
      }
    }
  }

  const height = parse_number(source.height ?? pick_pref(source, 'height'));
  if (height != null) {
    const cm = Math.round(height);
    if (cm >= JOURNEY_HEIGHT_MIN && cm <= JOURNEY_HEIGHT_MAX) {
      out.height_cm = cm;
    }
  }

  const weight = parse_number(source.weight ?? pick_pref(source, 'weight'));
  if (weight != null) {
    const kg = Math.round(weight);
    if (kg >= JOURNEY_WEIGHT_MIN && kg <= JOURNEY_WEIGHT_MAX) {
      out.weight_kg = kg;
    }
  }

  return out;
}
